// Caja de tesorería · Etapa 2 (docs/111, pedido de Rodrigo 2026-10-25). La caja mayor de Daniela: su
// turno del día (con lo que quedó en la caja fuerte), la posición de caja, las entregas de las cajas
// (contadas a ciegas), el depósito diario al banco (efectivo y cheques) y los retiros (para qué y a quién).
// Se cierra con arqueo ciego en Ventas › Caja › Mi turno y lo revisa Rodrigo en «Turnos y cierres».
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, nuevaClaveIdempotencia } from '../api/cliente';
import { PanelError } from '../comun/Estados';
import { Modal } from '../comun/Modal';
import { fecha, fechaHora, pesos } from '../comun/formato';
import { useSesion } from '../sesion/SesionContexto';
import { useCuentasEmpresa } from './datosPagos';

interface Movimiento {
  id: string;
  tipo: 'INGRESO' | 'RETIRO';
  medio: 'EFECTIVO' | 'CHEQUE';
  categoria: string;
  monto: string;
  motivo: string;
  entregadoA: string | null;
  por: string;
  creadoEn: string;
}
interface Estado {
  abierta: boolean;
  turno: {
    turnoId: string;
    numero: string;
    cajero: string;
    esMio: boolean;
    abiertoEn: string;
    fondoInicial: string;
    efectivo: string;
    cheques: string;
    movimientos: Movimiento[];
  } | null;
}
interface Posicion {
  tesoreria: { abierta: boolean; cajero: string | null; efectivo: string; cheques: string };
  cajas: Array<{
    turnoId: string;
    turno: string;
    punto: string;
    cajero: string;
    efectivo: string;
    cheques: string;
  }>;
  entregasPorRecibir: { cantidad: number; efectivo: string; cheques: string };
  cheques: { enCartera: string; depositados: string; protestados: string };
  rutasPorRecibir: { cantidad: number; efectivo: string; cheques: string; transferencias: string };
  transferenciasPorVerificar: { cantidad: number; monto: string };
  faltantesRuta: { cantidad: number; neto: string };
}
interface ChequeEntrega {
  chequeId: string;
  cliente: string;
  banco: string | null;
  numero: string | null;
  fechaDocumento: string;
  monto: string;
}
interface Entrega {
  entregaId: string;
  turno: string;
  punto: string;
  cajero: string;
  creadoEn: string;
  efectivoDeclarado: string;
  chequesDeclarado: string;
  chequesCantidad: number;
  cheques: ChequeEntrega[];
}
interface EntregaRecibida {
  entregaId: string;
  turno: string;
  cajero: string;
  efectivoDeclarado: string;
  efectivoContado: string;
  diferenciaEfectivo: string;
  chequesRecibido: string;
  chequesFaltan: number;
  recibidoPor: string;
  recibidoEn: string;
}
interface ChequeCartera extends ChequeEntrega {
  estado: string;
  ubicacion: 'TESORERIA' | 'CAJA' | 'ENTREGA_POR_RECIBIR' | 'NO_LLEGO';
  cajero: string | null;
}
interface Deposito {
  depositoId: string;
  fecha: string;
  cuentaBancariaId: string;
  efectivo: string;
  cheques: string;
  chequesCantidad: number;
  comprobante: string | null;
  por: string;
}

const CATEGORIA: Record<string, string> = {
  OTRO: 'Otro',
  SALDO_ANTERIOR: 'Saldo anterior',
  ENTREGA_CAJA: 'Entrega de caja',
  DEPOSITO: 'Depósito',
  PAGO_PROVEEDOR: 'Pago a proveedor',
  RETIRO_DUENO: 'Retiro del dueño',
  GASTO: 'Gasto',
  ADELANTO: 'Adelanto',
  FONDO_CAJA: 'Fondo a una caja',
  DIFERENCIA_RUTA: 'Diferencia de ruta',
};
const RETIROS = ['RETIRO_DUENO', 'GASTO', 'ADELANTO', 'FONDO_CAJA', 'OTRO'] as const;
const UBICACION: Record<ChequeCartera['ubicacion'], string> = {
  TESORERIA: 'En tesorería',
  CAJA: 'En una caja abierta',
  ENTREGA_POR_RECIBIR: 'En una entrega por recibir',
  NO_LLEGO: 'No llegó a tesorería',
};
const soloDigitos = (t: string) => t.replace(/\D/g, '');
const hoy = () => new Date().toLocaleDateString('sv-SE');

function useEstado(empresaId: string) {
  return useQuery({
    queryKey: ['emp', empresaId, 'tesoreria', 'caja'],
    queryFn: () => api.get<Estado>('/tesoreria/caja', { query: { empresaId } }),
    refetchInterval: 30_000,
  });
}

export function CajaTesoreria() {
  const [pestana, setPestana] = useState<'caja' | 'entregas' | 'depositar' | 'retiro'>('caja');
  return (
    <div
      className="pantalla consulta recepcion-rutas caja-tesoreria"
      data-pantalla="caja-tesoreria"
    >
      <h2>Caja de tesorería</h2>
      <p className="apagado">
        La caja mayor: recibe las cajas y las rutas, deposita en el banco y entrega efectivo. Lo que
        queda al cierre pasa al día siguiente.
      </p>
      <div className="acciones-fila" role="tablist">
        {(
          [
            ['caja', 'Caja y posición'],
            ['entregas', 'Entregas de cajas'],
            ['depositar', 'Depositar'],
            ['retiro', 'Retiros'],
          ] as const
        ).map(([v, t]) => (
          <button
            key={v}
            type="button"
            role="tab"
            aria-selected={pestana === v}
            className={pestana === v ? 'primario' : ''}
            onClick={() => setPestana(v)}
          >
            {t}
          </button>
        ))}
      </div>
      {pestana === 'caja' ? (
        <CajaYPosicion />
      ) : pestana === 'entregas' ? (
        <Entregas />
      ) : pestana === 'depositar' ? (
        <Depositar />
      ) : (
        <Retiros />
      )}
    </div>
  );
}

// ---------------------------------------------------------------- caja y posición

function CajaYPosicion() {
  const { empresa, puede } = useSesion();
  const empresaId = empresa!.id;
  const qc = useQueryClient();
  const estado = useEstado(empresaId);
  const posicion = useQuery({
    queryKey: ['emp', empresaId, 'tesoreria', 'posicion'],
    queryFn: () => api.get<Posicion>('/tesoreria/caja/posicion', { query: { empresaId } }),
    refetchInterval: 30_000,
  });
  const abrir = useMutation({
    mutationFn: () =>
      api.post(
        '/tesoreria/caja/abrir',
        { empresaId },
        { idempotencia: nuevaClaveIdempotencia('abrir-tesoreria') },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['emp', empresaId] }),
  });
  const t = estado.data?.turno ?? null;
  const p = posicion.data;
  return (
    <>
      {(estado.isError || posicion.isError) && (
        <PanelError error={estado.error ?? posicion.error} />
      )}
      <section className="recuadro">
        <h3>Caja de tesorería</h3>
        {!estado.data ? (
          <p className="apagado">Cargando…</p>
        ) : !t ? (
          <div className="acciones-fila">
            <span>Cerrada. Al abrirla entra lo que quedó en la caja fuerte al último cierre.</span>
            {puede('tesoreria.caja.operar') && (
              <button
                type="button"
                className="primario"
                disabled={abrir.isPending}
                onClick={() => abrir.mutate()}
              >
                Abrir caja de tesorería
              </button>
            )}
          </div>
        ) : (
          <table className="grilla compacta" aria-label="Caja de tesorería">
            <tbody>
              <tr>
                <th>Turno</th>
                <td className="mono">
                  {t.numero} · {t.cajero}
                  {t.esMio ? ' (tú)' : ''} · desde {fechaHora(t.abiertoEn)}
                </td>
              </tr>
              <tr>
                <th>Saldo inicial (caja fuerte)</th>
                <td className="mono der">{pesos(t.fondoInicial)}</td>
              </tr>
              <tr>
                <th>Efectivo que debería haber</th>
                <td className="mono der">
                  <b>{pesos(t.efectivo)}</b>
                </td>
              </tr>
              <tr>
                <th>Cheques que debería haber</th>
                <td className="mono der">
                  <b>{pesos(t.cheques)}</b>
                </td>
              </tr>
            </tbody>
          </table>
        )}
        {abrir.isError && <PanelError error={abrir.error} alCerrar={() => abrir.reset()} />}
        {t?.esMio && (
          <p className="apagado">
            Se cierra con arqueo ciego en Ventas › Caja › Mi turno de caja; lo revisa otra persona.
          </p>
        )}
      </section>
      {p && (
        <section className="recuadro">
          <h3>Posición de caja</h3>
          <table className="grilla compacta" aria-label="Posición de caja">
            <thead>
              <tr>
                <th>Dónde</th>
                <th className="der">Efectivo</th>
                <th className="der">Cheques</th>
                <th className="der">Transferencias</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Caja de tesorería{p.tesoreria.abierta ? '' : ' (cerrada)'}</td>
                <td className="der mono">{pesos(p.tesoreria.efectivo)}</td>
                <td className="der mono">{pesos(p.tesoreria.cheques)}</td>
                <td />
              </tr>
              {p.cajas.map((c) => (
                <tr key={c.turnoId}>
                  <td>
                    {c.punto} · {c.cajero} <span className="apagado mono">{c.turno}</span>
                  </td>
                  <td className="der mono">{pesos(c.efectivo)}</td>
                  <td className="der mono">{pesos(c.cheques)}</td>
                  <td />
                </tr>
              ))}
              <tr>
                <td>Entregas de cajas por recibir ({p.entregasPorRecibir.cantidad})</td>
                <td className="der mono">{pesos(p.entregasPorRecibir.efectivo)}</td>
                <td className="der mono">{pesos(p.entregasPorRecibir.cheques)}</td>
                <td />
              </tr>
              <tr>
                <td>Rutas por recibir ({p.rutasPorRecibir.cantidad})</td>
                <td className="der mono">{pesos(p.rutasPorRecibir.efectivo)}</td>
                <td className="der mono">{pesos(p.rutasPorRecibir.cheques)}</td>
                <td className="der mono">{pesos(p.rutasPorRecibir.transferencias)}</td>
              </tr>
              <tr>
                <td>
                  Transferencias de ruta por verificar ({p.transferenciasPorVerificar.cantidad})
                </td>
                <td />
                <td />
                <td className="der mono">{pesos(p.transferenciasPorVerificar.monto)}</td>
              </tr>
            </tbody>
          </table>
          <table className="grilla compacta" aria-label="Cartera de cheques">
            <tbody>
              <tr>
                <th>Cheques en cartera (sin depositar)</th>
                <td className="der mono">{pesos(p.cheques.enCartera)}</td>
                <th>Depositados por confirmar</th>
                <td className="der mono">{pesos(p.cheques.depositados)}</td>
                <th>Protestados</th>
                <td className="der mono">{pesos(p.cheques.protestados)}</td>
              </tr>
              <tr>
                <th>Faltantes de ruta por resolver ({p.faltantesRuta.cantidad})</th>
                <td className="der mono">{pesos(p.faltantesRuta.neto)}</td>
                <td colSpan={4} />
              </tr>
            </tbody>
          </table>
        </section>
      )}
      {t && (
        <section className="recuadro">
          <h3>Movimientos del turno</h3>
          <table className="grilla compacta" aria-label="Movimientos de tesorería">
            <thead>
              <tr>
                <th>Hora</th>
                <th>Qué</th>
                <th>Detalle</th>
                <th>Medio</th>
                <th className="der">Entra</th>
                <th className="der">Sale</th>
                <th>Por</th>
              </tr>
            </thead>
            <tbody>
              {t.movimientos.map((m) => (
                <tr key={m.id}>
                  <td>{fechaHora(m.creadoEn)}</td>
                  <td>{CATEGORIA[m.categoria] ?? m.categoria}</td>
                  <td>
                    {m.motivo}
                    {m.entregadoA ? ` · a ${m.entregadoA}` : ''}
                  </td>
                  <td>{m.medio === 'CHEQUE' ? 'Cheques' : 'Efectivo'}</td>
                  <td className="der mono">{m.tipo === 'INGRESO' ? pesos(m.monto) : ''}</td>
                  <td className="der mono">{m.tipo === 'RETIRO' ? pesos(m.monto) : ''}</td>
                  <td>{m.por}</td>
                </tr>
              ))}
              {!t.movimientos.length && (
                <tr>
                  <td colSpan={7} className="vacio">
                    Sin movimientos (los cobros del turno se ven en Mi turno de caja).
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </section>
      )}
    </>
  );
}

// ---------------------------------------------------------------- entregas de cajas

function Entregas() {
  const { empresa } = useSesion();
  const empresaId = empresa!.id;
  const q = useQuery({
    queryKey: ['emp', empresaId, 'tesoreria', 'entregas'],
    queryFn: () => api.get<Entrega[]>('/tesoreria/caja/entregas', { query: { empresaId } }),
    refetchInterval: 30_000,
  });
  const recibidas = useQuery({
    queryKey: ['emp', empresaId, 'tesoreria', 'entregas', 'recibidas'],
    queryFn: () =>
      api.get<EntregaRecibida[]>('/tesoreria/caja/entregas/recibidas', { query: { empresaId } }),
  });
  const [abierta, setAbierta] = useState<Entrega | null>(null);
  return (
    <>
      {q.isError && <PanelError error={q.error} />}
      <section className="recuadro">
        <h3>Por recibir</h3>
        <table className="grilla compacta" aria-label="Entregas por recibir">
          <thead>
            <tr>
              <th>Cerrada</th>
              <th>Caja</th>
              <th>Cajero</th>
              <th className="der">Cheques</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {(q.data ?? []).map((x) => (
              <tr key={x.entregaId}>
                <td>{fechaHora(x.creadoEn)}</td>
                <td>
                  {x.punto} <span className="mono apagado">{x.turno}</span>
                </td>
                <td>{x.cajero}</td>
                <td className="der">{x.cheques.length}</td>
                <td>
                  <button type="button" className="primario" onClick={() => setAbierta(x)}>
                    Recibir
                  </button>
                </td>
              </tr>
            ))}
            {q.isSuccess && !q.data.length && (
              <tr>
                <td colSpan={5} className="vacio">
                  No hay entregas por recibir.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </section>
      <section className="recuadro">
        <h3>Recibidas (últimos 30 días)</h3>
        <table className="grilla compacta" aria-label="Entregas recibidas">
          <thead>
            <tr>
              <th>Recibida</th>
              <th>Caja</th>
              <th>Cajero</th>
              <th className="der">Declarado</th>
              <th className="der">Contado</th>
              <th className="der">Diferencia</th>
              <th className="der">Cheques</th>
              <th>Recibió</th>
            </tr>
          </thead>
          <tbody>
            {(recibidas.data ?? []).map((x) => (
              <tr key={x.entregaId}>
                <td>{fechaHora(x.recibidoEn)}</td>
                <td className="mono">{x.turno}</td>
                <td>{x.cajero}</td>
                <td className="der mono">{pesos(x.efectivoDeclarado)}</td>
                <td className="der mono">{pesos(x.efectivoContado)}</td>
                <td className={`der mono${Number(x.diferenciaEfectivo) ? ' texto-aviso' : ''}`}>
                  {pesos(x.diferenciaEfectivo)}
                </td>
                <td className="der mono">
                  {pesos(x.chequesRecibido)}
                  {x.chequesFaltan > 0 && (
                    <span className="texto-aviso"> · faltan {x.chequesFaltan}</span>
                  )}
                </td>
                <td>{x.recibidoPor}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
      {abierta && <RecibirEntrega entrega={abierta} alCerrar={() => setAbierta(null)} />}
    </>
  );
}

function RecibirEntrega({ entrega: x, alCerrar }: { entrega: Entrega; alCerrar: () => void }) {
  const { empresa } = useSesion();
  const empresaId = empresa!.id;
  const qc = useQueryClient();
  const [contado, setContado] = useState('');
  const [revelado, setRevelado] = useState(false);
  const [llego, setLlego] = useState<Set<string>>(new Set());
  const [observacion, setObservacion] = useState('');
  const [clave] = useState(() => nuevaClaveIdempotencia('recibir-entrega'));
  const recibir = useMutation({
    mutationFn: () =>
      api.post(
        `/tesoreria/caja/entregas/${x.entregaId}/recibir`,
        {
          empresaId,
          efectivoContado: contado || '0',
          chequesRecibidos: [...llego],
          observacion: observacion.trim() || null,
        },
        { idempotencia: clave },
      ),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['emp', empresaId] });
      alCerrar();
    },
  });
  const dif = Number(contado || '0') - Number(x.efectivoDeclarado);
  return (
    <Modal
      titulo={`Recibir entrega · ${x.punto} · ${x.cajero}`}
      alCerrar={alCerrar}
      pie={
        <>
          <button type="button" onClick={alCerrar}>
            Volver
          </button>
          <button
            type="button"
            className="primario"
            disabled={!revelado || recibir.isPending}
            onClick={() => recibir.mutate()}
          >
            {revelado ? 'Confirmar recepción' : 'Primero cuenta el efectivo'}
          </button>
        </>
      }
    >
      <label className="campo">
        <span className="etiqueta">Efectivo contado (sin mirar lo declarado)</span>
        <input
          aria-label="Efectivo contado"
          inputMode="numeric"
          data-autofoco
          disabled={revelado}
          value={contado}
          onChange={(e) => setContado(soloDigitos(e.target.value))}
        />
      </label>
      {!revelado ? (
        <button type="button" disabled={contado === ''} onClick={() => setRevelado(true)}>
          Contado listo
        </button>
      ) : (
        <p>
          Declarado por la caja: <b>{pesos(x.efectivoDeclarado)}</b> ·{' '}
          <span className={dif === 0 ? 'texto-ok' : 'texto-aviso'}>
            {dif === 0
              ? 'cuadra'
              : dif < 0
                ? `faltan ${pesos(String(-dif))}`
                : `sobran ${pesos(String(dif))}`}
          </span>{' '}
          <button type="button" onClick={() => setRevelado(false)}>
            Volver a contar
          </button>
        </p>
      )}
      {x.cheques.length > 0 && (
        <table className="grilla compacta" aria-label="Cheques de la entrega">
          <thead>
            <tr>
              <th>Llegó</th>
              <th>Cliente</th>
              <th>Banco</th>
              <th>N°</th>
              <th className="der">Monto</th>
            </tr>
          </thead>
          <tbody>
            {x.cheques.map((c) => (
              <tr key={c.chequeId}>
                <td>
                  <input
                    type="checkbox"
                    aria-label={`Llegó el cheque ${c.numero ?? ''} de ${c.cliente}`}
                    checked={llego.has(c.chequeId)}
                    onChange={(e) =>
                      setLlego((s) => {
                        const n = new Set(s);
                        if (e.target.checked) n.add(c.chequeId);
                        else n.delete(c.chequeId);
                        return n;
                      })
                    }
                  />
                </td>
                <td>{c.cliente}</td>
                <td>{c.banco ?? '—'}</td>
                <td className="mono">{c.numero ?? '—'}</td>
                <td className="der mono">{pesos(c.monto)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <label className="campo">
        <span className="etiqueta">Observación (opcional)</span>
        <input
          aria-label="Observación"
          maxLength={500}
          value={observacion}
          onChange={(e) => setObservacion(e.target.value)}
        />
      </label>
      {recibir.isError && <PanelError error={recibir.error} />}
    </Modal>
  );
}

// ---------------------------------------------------------------- depositar

function Depositar() {
  const { empresa } = useSesion();
  const empresaId = empresa!.id;
  const qc = useQueryClient();
  const estado = useEstado(empresaId);
  const cuentas = useCuentasEmpresa(empresaId);
  const activas = (cuentas.data ?? []).filter((c) => c.activa);
  const cheques = useQuery({
    queryKey: ['emp', empresaId, 'tesoreria', 'cheques-caja'],
    queryFn: () => api.get<ChequeCartera[]>('/tesoreria/caja/cheques', { query: { empresaId } }),
  });
  const depositos = useQuery({
    queryKey: ['emp', empresaId, 'tesoreria', 'depositos'],
    queryFn: () => api.get<Deposito[]>('/tesoreria/caja/depositos', { query: { empresaId } }),
  });
  const [cuenta, setCuenta] = useState('');
  const [dia, setDia] = useState(hoy());
  const [efectivo, setEfectivo] = useState('');
  const [elegidos, setElegidos] = useState<Set<string>>(new Set());
  const [comprobante, setComprobante] = useState('');
  const [hecho, setHecho] = useState<string | null>(null);
  const cuentaElegida = cuenta || activas[0]?.id || '';
  const enTesoreria = (cheques.data ?? []).filter((c) => c.ubicacion === 'TESORERIA');
  const otros = (cheques.data ?? []).filter((c) => c.ubicacion !== 'TESORERIA');
  const totalCheques = enTesoreria
    .filter((c) => elegidos.has(c.chequeId))
    .reduce((s, c) => s + Number(c.monto), 0);
  const depositar = useMutation({
    mutationFn: () =>
      api.post<{ efectivo: string; cheques: string }>(
        '/tesoreria/caja/depositos',
        {
          empresaId,
          cuentaBancariaId: cuentaElegida,
          fecha: dia,
          efectivo: efectivo || '0',
          chequeIds: [...elegidos],
          comprobante: comprobante.trim() || null,
        },
        { idempotencia: nuevaClaveIdempotencia('deposito') },
      ),
    onSuccess: async (r) => {
      setHecho(
        `Depósito registrado: ${pesos(r.efectivo)} en efectivo y ${pesos(r.cheques)} en cheques.`,
      );
      setEfectivo('');
      setElegidos(new Set());
      setComprobante('');
      await qc.invalidateQueries({ queryKey: ['emp', empresaId] });
    },
  });
  const nombreCuenta = (id: string) => (cuentas.data ?? []).find((c) => c.id === id)?.texto ?? '—';
  return (
    <>
      {hecho && (
        <p className="texto-ok" role="status">
          {hecho}
        </p>
      )}
      <section className="recuadro">
        <h3>Nuevo depósito</h3>
        <div className="acciones-fila">
          <label className="campo-linea">
            Cuenta{' '}
            <select
              aria-label="Cuenta de depósito"
              value={cuentaElegida}
              onChange={(e) => setCuenta(e.target.value)}
            >
              {!activas.length && <option value="">Sin cuentas bancarias de la empresa</option>}
              {activas.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.texto}
                </option>
              ))}
            </select>
          </label>
          <label className="campo-linea">
            Fecha{' '}
            <input
              type="date"
              aria-label="Fecha del depósito"
              value={dia}
              onChange={(e) => setDia(e.target.value)}
            />
          </label>
          <label className="campo-linea">
            Efectivo{' '}
            <input
              aria-label="Efectivo a depositar"
              inputMode="numeric"
              className="mono der"
              value={efectivo}
              onChange={(e) => setEfectivo(soloDigitos(e.target.value))}
            />
          </label>
          <span className="apagado">
            en caja {estado.data?.turno ? pesos(estado.data.turno.efectivo) : '—'}
          </span>
          <label className="campo-linea">
            Comprobante{' '}
            <input
              aria-label="Comprobante"
              maxLength={60}
              value={comprobante}
              onChange={(e) => setComprobante(e.target.value)}
            />
          </label>
        </div>
        <table className="grilla compacta" aria-label="Cheques en tesorería">
          <thead>
            <tr>
              <th />
              <th>Fecha cheque</th>
              <th>Cliente</th>
              <th>Banco</th>
              <th>N°</th>
              <th className="der">Monto</th>
            </tr>
          </thead>
          <tbody>
            {enTesoreria.map((c) => (
              <tr key={c.chequeId}>
                <td>
                  <input
                    type="checkbox"
                    aria-label={`Depositar cheque ${c.numero ?? ''} de ${c.cliente}`}
                    checked={elegidos.has(c.chequeId)}
                    onChange={(e) =>
                      setElegidos((s) => {
                        const n = new Set(s);
                        if (e.target.checked) n.add(c.chequeId);
                        else n.delete(c.chequeId);
                        return n;
                      })
                    }
                  />
                </td>
                <td className={c.fechaDocumento > hoy() ? 'texto-aviso' : ''}>
                  {fecha(c.fechaDocumento)}
                  {c.fechaDocumento > hoy() ? ' · a fecha' : ''}
                </td>
                <td>{c.cliente}</td>
                <td>{c.banco ?? '—'}</td>
                <td className="mono">{c.numero ?? '—'}</td>
                <td className="der mono">{pesos(c.monto)}</td>
              </tr>
            ))}
            {cheques.isSuccess && !enTesoreria.length && (
              <tr>
                <td colSpan={6} className="vacio">
                  No hay cheques en tesorería.
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {otros.length > 0 && (
          <p className="apagado">
            {otros.length} cheque(s) en cartera todavía no están en tesorería (
            {[...new Set(otros.map((c) => UBICACION[c.ubicacion].toLowerCase()))].join(', ')}).
          </p>
        )}
        {depositar.isError && (
          <PanelError error={depositar.error} alCerrar={() => depositar.reset()} />
        )}
        <div className="acciones-fila">
          <span className="espaciador" />
          <span>
            Total: <b className="mono">{pesos(String(Number(efectivo || '0') + totalCheques))}</b>
          </span>
          <button
            type="button"
            className="primario"
            disabled={
              !cuentaElegida || depositar.isPending || (!Number(efectivo) && !elegidos.size)
            }
            onClick={() => depositar.mutate()}
          >
            Registrar depósito
          </button>
        </div>
      </section>
      <section className="recuadro">
        <h3>Depósitos (últimos 60 días)</h3>
        <table className="grilla compacta" aria-label="Depósitos de tesorería">
          <thead>
            <tr>
              <th>Fecha</th>
              <th>Cuenta</th>
              <th className="der">Efectivo</th>
              <th className="der">Cheques</th>
              <th>Comprobante</th>
              <th>Por</th>
            </tr>
          </thead>
          <tbody>
            {(depositos.data ?? []).map((d) => (
              <tr key={d.depositoId}>
                <td>{fecha(d.fecha)}</td>
                <td>{nombreCuenta(d.cuentaBancariaId)}</td>
                <td className="der mono">{pesos(d.efectivo)}</td>
                <td className="der mono">
                  {pesos(d.cheques)}{' '}
                  {d.chequesCantidad ? (
                    <span className="apagado">({d.chequesCantidad})</span>
                  ) : null}
                </td>
                <td className="mono">{d.comprobante ?? '—'}</td>
                <td>{d.por}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </>
  );
}

// ---------------------------------------------------------------- retiros

function Retiros() {
  const { empresa, puede } = useSesion();
  const empresaId = empresa!.id;
  const qc = useQueryClient();
  const estado = useEstado(empresaId);
  const [monto, setMonto] = useState('');
  const [categoria, setCategoria] = useState<(typeof RETIROS)[number]>('RETIRO_DUENO');
  const [entregadoA, setEntregadoA] = useState('');
  const [motivo, setMotivo] = useState('');
  const [hecho, setHecho] = useState<string | null>(null);
  const retirar = useMutation({
    mutationFn: () =>
      api.post(
        '/tesoreria/caja/retiros',
        {
          empresaId,
          monto,
          categoria,
          entregadoA: entregadoA.trim() || null,
          motivo: motivo.trim() || null,
        },
        { idempotencia: nuevaClaveIdempotencia('retiro-tesoreria') },
      ),
    onSuccess: async () => {
      setHecho(
        `Retiro de ${pesos(monto)}${entregadoA.trim() ? ` entregado a ${entregadoA.trim()}` : ''}.`,
      );
      setMonto('');
      setEntregadoA('');
      setMotivo('');
      await qc.invalidateQueries({ queryKey: ['emp', empresaId] });
    },
  });
  if (!puede('tesoreria.retiro.registrar'))
    return <p className="apagado">No tienes permiso para registrar retiros.</p>;
  const retiros = (estado.data?.turno?.movimientos ?? []).filter(
    (m) => m.tipo === 'RETIRO' && (RETIROS as readonly string[]).includes(m.categoria),
  );
  return (
    <>
      {hecho && (
        <p className="texto-ok" role="status">
          {hecho}
        </p>
      )}
      <section className="recuadro">
        <h3>Nuevo retiro de efectivo</h3>
        <div className="acciones-fila">
          <label className="campo-linea">
            Monto{' '}
            <input
              aria-label="Monto del retiro"
              inputMode="numeric"
              className="mono der"
              value={monto}
              onChange={(e) => setMonto(soloDigitos(e.target.value))}
            />
          </label>
          <label className="campo-linea">
            Para qué{' '}
            <select
              aria-label="Categoría del retiro"
              value={categoria}
              onChange={(e) => setCategoria(e.target.value as typeof categoria)}
            >
              {RETIROS.map((c) => (
                <option key={c} value={c}>
                  {CATEGORIA[c]}
                </option>
              ))}
            </select>
          </label>
          <label className="campo-linea">
            A quién (opcional){' '}
            <input
              aria-label="A quién se entrega"
              maxLength={120}
              value={entregadoA}
              onChange={(e) => setEntregadoA(e.target.value)}
            />
          </label>
          <label className="campo-linea">
            Detalle (opcional){' '}
            <input
              aria-label="Detalle del retiro"
              maxLength={300}
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
            />
          </label>
          <span className="apagado">
            en caja {estado.data?.turno ? pesos(estado.data.turno.efectivo) : '—'}
          </span>
          <button
            type="button"
            className="primario"
            disabled={!Number(monto) || retirar.isPending}
            onClick={() => retirar.mutate()}
          >
            Registrar retiro
          </button>
        </div>
        {retirar.isError && <PanelError error={retirar.error} alCerrar={() => retirar.reset()} />}
      </section>
      <section className="recuadro">
        <h3>Retiros del turno</h3>
        <table className="grilla compacta" aria-label="Retiros del turno">
          <thead>
            <tr>
              <th>Hora</th>
              <th>Para qué</th>
              <th>A quién</th>
              <th>Detalle</th>
              <th className="der">Monto</th>
              <th>Por</th>
            </tr>
          </thead>
          <tbody>
            {retiros.map((m) => (
              <tr key={m.id}>
                <td>{fechaHora(m.creadoEn)}</td>
                <td>{CATEGORIA[m.categoria]}</td>
                <td>{m.entregadoA ?? '—'}</td>
                <td>{m.motivo}</td>
                <td className="der mono">{pesos(m.monto)}</td>
                <td>{m.por}</td>
              </tr>
            ))}
            {!retiros.length && (
              <tr>
                <td colSpan={6} className="vacio">
                  Sin retiros en este turno.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </section>
    </>
  );
}
